import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Button from "../../../../components/ui/Button";
import Skeleton from "../../../../components/ui/Skeleton";
import MentionAutocomplete from "../../../../components/MentionAutocomplete";
import "./ReplyBox.css";
import ReplyItem from "./ReplyItem";
import {
  fetchCommentReplies,
  createReply,
  updateReplyThunk,
  toggleReplyLikeAsync,
  hydrateRepliesFromCache,
} from "../../../../redux/slices/posts/commentSlice.js";
import { patchReplyLikeCount } from "../../../../redux/slices/posts/commentSlice.js";
import { getDisplayName } from "../../../../utils/userDisplay";
import {
  useNotificationTriggers,
  parseMentions,
  hasMentions,
  convertLegacyMentions,
  getMentionedUserIds,
} from "../../../../features/notifications";
import { injectStructuredMentions } from "../../../../utils/mentionInjection.js";

const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_OBJECT = Object.freeze({});

/** Maximum characters allowed in a reply — mirrored in ReplyItem and Firestore rules. */
const REPLY_MAX_LENGTH = 2000;

// ─── Top-level Reply Input ─────────────────────────────────────────────────────
/**
 * Textarea + submit button rendered at the top of the reply thread.
 * This is the primary input for replying directly to a comment.
 * Reply-to-reply inputs are handled entirely within ReplyItem.
 */
const ReplyInput = ({ onSubmit, isSubmitting, isParentOptimistic, initialState = { text: "", mentionMap: {} }, onClear }) => {
  const [replyText, setReplyText] = useState(initialState.text || "");
  const [mentionMap, setMentionMap] = useState(initialState.mentionMap || {});
  const replyInputRef = useRef(null);

  // Sync when initialState changes (e.g. @mention auto-fill from a notification)
  useEffect(() => {
    if (initialState?.text) {
      setReplyText(initialState.text);
      setMentionMap(initialState.mentionMap || {});
      if (replyInputRef.current) {
        try {
          replyInputRef.current.focus();
          const len = initialState.text.length;
          replyInputRef.current.setSelectionRange(len, len);
        } catch (err) {
          if (process.env.NODE_ENV !== "production") {
            console.error("[ReplyBox] Error focusing reply input:", err);
          }
        }
      }
    }
  }, [initialState]);

  const handleMentionSelect = useCallback((user) => {
    if (!user || !user.username || !user.id) return;
    setMentionMap((prev) => ({ ...prev, [user.username]: user.id }));
  }, []);

  const handleSubmit = useCallback(async () => {
    const trimmed = replyText.trim();
    if (!trimmed) return;
    const injectedText = injectStructuredMentions(trimmed, mentionMap);
    try {
      await onSubmit(injectedText);
      // Only clear on success — keeps text visible on failure so user can retry
      setReplyText("");
      setMentionMap({});
      if (replyInputRef.current) {
        try {
          replyInputRef.current.style.height = "auto";
        } catch (err) {
          if (process.env.NODE_ENV !== "production") {
            console.error("[ReplyBox] Error resetting textarea height:", err);
          }
        }
      }
      if (onClear) onClear();
    } catch {
      // Submit failed — text stays in input for retry
    }
  }, [replyText, onSubmit, onClear]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (replyText.trim() && !isSubmitting) {
        handleSubmit();
      }
    }
  };

  const nearLimit = replyText.length >= REPLY_MAX_LENGTH * 0.9;
  const atLimit = replyText.length >= REPLY_MAX_LENGTH;

  return (
    <div className="reply-input">
      <MentionAutocomplete
        ref={replyInputRef}
        className="reply-textarea form-textarea"
        placeholder="Write a reply…"
        value={replyText}
        maxLength={REPLY_MAX_LENGTH}
        onChange={(newValue) => {
          setReplyText(newValue);
        }}
        onKeyDown={handleKeyDown}
        onMentionSelect={handleMentionSelect}
        mentionNames={Object.keys(mentionMap)}
        aria-label="Write a reply"
      />
      <div className="reply-input-bottom">
        <div className="char-counter-row">
          <span
            className={`char-counter${atLimit ? " at-limit" : nearLimit ? " near-limit" : ""}`}
          >
            {replyText.length}/{REPLY_MAX_LENGTH}
          </span>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={!replyText.trim() || isSubmitting || isParentOptimistic}
          className="post-reply-button"
          title={isParentOptimistic ? "Saving reply — try again in a moment" : undefined}
        >
          {isSubmitting ? "Posting…" : "Reply"}
        </Button>
      </div>
    </div>
  );
};

ReplyInput.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  isParentOptimistic: PropTypes.bool,
  initialState: PropTypes.shape({
    text: PropTypes.string,
    mentionMap: PropTypes.object,
  }),
  onClear: PropTypes.func,
};

// ─── ReplyBox ──────────────────────────────────────────────────────────────────
/**
 * Orchestrates the reply thread for a single comment.
 *
 * Responsibilities:
 *  - Fetch / cache-hydrate replies on mount
 *  - Provide async handlers (edit, like, submit) to each ReplyItem via props
 *  - Render the top-level ReplyInput and paginated reply list
 *
 * All per-item UI state (options menu, edit mode, inline reply-to-reply) lives
 * inside ReplyItem — this component no longer holds any of that state.
 */
const ReplyBox = ({ postId, commentId, isVisible, replyToUser }) => {
  const dispatch = useDispatch();

  const isParentOptimistic =
    typeof commentId === "string" && commentId.startsWith("temp-");

  // ── Redux selectors ──────────────────────────────────────────────────────────
  const replies = useSelector(
    (state) => state.replies?.items?.[commentId] ?? EMPTY_ARRAY,
  );
  const visibleReplies = useMemo(
    () => (Array.isArray(replies) ? replies.filter((reply) => !reply?.deletedAt) : []),
    [replies],
  );
  const replyLikePending = useSelector(
    (state) => state.commentLikes?.replyLikePending ?? EMPTY_OBJECT,
  );
  const likedReplyIds = useSelector(
    (state) => state.commentLikes?.likedReplyIds ?? EMPTY_OBJECT,
  );
  const commentForNotify = useSelector((state) => {
    const list = state.comments?.items?.[postId];
    return Array.isArray(list)
      ? list.find((c) => c.id === commentId)
      : undefined;
  });

  // Keep a ref to replies so handleEditSave doesn't need `replies` in deps
  const repliesRef = useRef(replies);
  useEffect(() => {
    repliesRef.current = replies;
  }, [replies]);

  // Ref for the replies list container — used to auto-scroll to newly created replies
  const repliesListRef = useRef(null);

  const currentUser = useSelector(
    (state) => state.auth.currentUser ?? EMPTY_OBJECT,
  );
  const userData = useSelector((state) => state.auth.userData ?? EMPTY_OBJECT);

  // ── Local state ──────────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [displayCount, setDisplayCount] = useState(8);

  const { triggerReplyNotification, triggerMentionNotification, triggerCommentLikeNotification } =
    useNotificationTriggers();

  // Reset display count when commentId changes (reply box opened for a different comment)
  useEffect(() => {
    setDisplayCount(8);
  }, [commentId]);

  // ── Fetch replies on mount / visibility change ───────────────────────────────
  // R-03: Added isMounted flag to prevent setting state on unmounted components
  useEffect(() => {
    let isMounted = true;
    const loadReplies = async () => {
      if (!isVisible || !commentId) return;
      if (isMounted) setIsLoading(true);
      try {
        try {
          dispatch(hydrateRepliesFromCache(commentId));
        } catch (err) {
          if (process.env.NODE_ENV !== "production") {
            console.error("[ReplyBox] Error hydrating replies from cache:", err);
          }
        }
        await dispatch(fetchCommentReplies(commentId))
          .unwrap()
          .catch((err) => console.error("[ReplyBox] fetchCommentReplies failed:", err));
      } catch (error) {
        if (isMounted) {
          console.error("Failed to load replies:", error);
          toast.error("Failed to load replies");
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    loadReplies();
    return () => {
      isMounted = false;
    };
  }, [isVisible, commentId, dispatch]);

  // ── Handlers passed down to ReplyItem ───────────────────────────────────────

  /**
   * Called by ReplyItem when the user saves an edit.
   * @param {string} replyId
   * @param {string} newText
   */
  const handleEditSave = useCallback(
    async (replyId, newText) => {
      try {
        // Phase 6: Convert any legacy @username mentions to structured tokens
        const convertedText = await convertLegacyMentions(newText.trim());

        // Find the original reply text for mention diff
        const originalReply = repliesRef.current.find((r) => r.id === replyId);
        const originalText = originalReply?.text || "";

        await dispatch(
          updateReplyThunk({ commentId, replyId, text: convertedText }),
        ).unwrap();

        // Non-blocking mention notifications to keep edit UX instant.
        Promise.resolve()
          .then(async () => {
            const [oldUserIds, newUserIds] = await Promise.all([
              getMentionedUserIds(originalText),
              getMentionedUserIds(convertedText),
            ]);
            const newMentions = [...newUserIds].filter(
              (id) => !oldUserIds.has(id),
            );
            if (newMentions.length === 0) return;
            const currentUserId = currentUser?.uid;
            const filteredIds = newMentions.filter((id) => id !== currentUserId);
            if (filteredIds.length === 0) return;
            await triggerMentionNotification(filteredIds, {
              postId,
              commentId,
              type: "reply",
              text: convertedText.substring(0, 100),
            });
          })
          .catch((mentionError) => {
            console.warn(
              "Failed to send mention notifications on reply edit:",
              mentionError,
            );
          });

        toast.success("Reply updated successfully");
      } catch (error) {
        console.error("Error updating reply:", error);
        toast.error(error.message || "Failed to update reply");
      }
    },
    [dispatch, commentId, postId, currentUser?.uid, triggerMentionNotification],
  );

  /**
   * Called by ReplyItem when the user likes a reply.
   * @param {string} replyId
   */
  const handleReplyLike = useCallback(
    async (replyId) => {
      if (!currentUser.uid) {
        toast.error("Please log in to like replies");
        return;
      }

      // Find the reply to get author info for notification
      const reply = (repliesRef.current || []).find((r) => r.id === replyId);
      const alreadyLiked = !!likedReplyIds[replyId];
      const prevLikes =
        typeof reply?.likes === "number" ? reply.likes : 0;
      dispatch(
        patchReplyLikeCount({
          commentId,
          replyId,
          likes: alreadyLiked ? Math.max(0, prevLikes - 1) : prevLikes + 1,
        }),
      );

      try {
        await dispatch(toggleReplyLikeAsync({ replyId, commentId })).unwrap();

        // Notify reply author only when this action is a NEW like (not an unlike)
        // PHASE 2 FIX: Skip notification if reply is soft-deleted (FINDING-ORPHAN-3)
        if (
          !alreadyLiked &&
          !reply?.deletedAt &&
          reply?.userId &&
          reply.userId !== currentUser.uid
        ) {
          try {
            await triggerCommentLikeNotification(
              {
                commentId: commentId,
                id: replyId,
                postId: postId,
                text: reply.text,
                isReply: true,
              },
              reply.userId,
            );
          } catch (_) {
            /* Notification failure shouldn't break the like */
          }
        }
      } catch (error) {
        dispatch(
          patchReplyLikeCount({ commentId, replyId, likes: prevLikes }),
        );
        console.error("Failed to like reply:", error);
        toast.error("Failed to like reply");
      }
    },
    [dispatch, commentId, currentUser.uid, postId, likedReplyIds, triggerCommentLikeNotification],
  );

  /**
   * Called by the top-level ReplyInput AND by ReplyItem for reply-to-reply.
   * Generates a stable tempId for the optimistic insert in commentSlice.
   * @param {string} replyTextContent
   */
  const handleReplySubmit = useCallback(
    async (replyTextContent) => {
      if (!replyTextContent.trim()) return;
      if (!currentUser.uid) {
        toast.error("Please log in to reply to comments");
        return;
      }

      const tempId = `temp-reply-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;

      setIsSubmitting(true);

      // Bump the display count BEFORE dispatching so the optimistic reply
      // (which is pushed to the END of the list by createReply.pending)
      // is within the sliced render window and has a real DOM node to scroll to.
      setDisplayCount((prev) => prev + 1);

      dispatch(
        createReply({
          commentId,
          postId,
          tempId,
          replyData: {
            userId: currentUser.uid || null,
            text: replyTextContent,
            username: getDisplayName({
              profile: userData,
              authUser: currentUser,
              candidates: [
                userData?.username,
                userData?.displayName,
                userData?.name,
              ],
            }),
            profession: userData.userProfession || "",
            photoURL:
              userData.photoURL ||
              userData.userPhotoURL ||
              currentUser?.photoURL ||
              "",
          },
        })
      )
        .unwrap()
        .catch((error) => {
          // Roll back the display count bump on failure
          setDisplayCount((prev) => Math.max(8, prev - 1));
          console.error("Failed to submit reply:", error);
          toast.error("Failed to submit reply");
        })
        .finally(() => {
          setIsSubmitting(false);
        });

      // Scroll immediately after dispatch — the optimistic node is inserted
      // synchronously by createReply.pending in the Redux reducer.
      // Use requestAnimationFrame to let React flush the state update first,
      // then a short timeout to let the DOM paint the new element.
      requestAnimationFrame(() => {
        setTimeout(() => {
          const repliesList = repliesListRef.current;
          if (!repliesList) return;
          // First try to find the specific optimistic node by tempId
          const optimisticNode = repliesList.querySelector(`[data-reply-id="${tempId}"]`);
          if (optimisticNode) {
            optimisticNode.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            return;
          }
          // Fallback: scroll to the last reply in the list
          const replyElements = repliesList.querySelectorAll('[data-reply-id]');
          const lastReply = replyElements[replyElements.length - 1];
          if (lastReply) {
            lastReply.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 80);
      });

      try {

        // Non-blocking notifications so reply submit feels instant.
        Promise.allSettled([
          (async () => {
            const commentAuthorId = commentForNotify?.userId || null;
            const myId = currentUser?.uid;
            if (commentAuthorId && myId && commentAuthorId !== myId) {
              await triggerReplyNotification(
                { id: commentId, postId },
                { content: replyTextContent },
                commentAuthorId,
              );
            }
          })(),
          (async () => {
            if (!hasMentions(replyTextContent)) return;
            const mentionedUserIds = await parseMentions(replyTextContent);
            const myId = currentUser?.uid;
            if (mentionedUserIds.length > 0) {
              await triggerMentionNotification(
                mentionedUserIds.filter((id) => id !== myId),
                {
                  postId,
                  commentId,
                  type: "reply",
                  text: replyTextContent.substring(0, 100),
                },
              );
            }
          })(),
        ]).catch((notificationError) => {
          console.warn("Failed to dispatch reply notifications:", notificationError);
        });
      } catch {
        // Errors handled in the .catch() above
      }
    },
    [
      dispatch,
      commentId,
      postId,
      currentUser,
      userData,
      commentForNotify,
      triggerReplyNotification,
      triggerMentionNotification,
    ],
  );

  // T1: Pre-populate @mention when replyToUser is provided
  const replyInputInitialState = useMemo(() => {
    if (!replyToUser?.name) return { text: "", mentionMap: {} };
    const userId = replyToUser.userId || "unknown";
    return {
      text: `@${replyToUser.name} `,
      mentionMap: { [replyToUser.name]: userId },
    };
  }, [replyToUser]);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="replies-section">
      {/* Primary reply input — always shown at the top of the thread */}
      <ReplyInput
        onSubmit={handleReplySubmit}
        isSubmitting={isSubmitting}
        isParentOptimistic={isParentOptimistic}
        initialState={replyInputInitialState}
      />

      {isLoading ? (
        <div className="space-y-4 py-4">
          <Skeleton height={60} width="100%" className="rounded-lg" />
          <Skeleton height={60} width="100%" className="rounded-lg" />
        </div>
      ) : (
        <div className="replies-list" ref={repliesListRef}>
          {visibleReplies.slice(0, displayCount).map((reply) => (
            <ReplyItem
              key={reply.id}
              reply={reply}
              commentId={commentId}
              postId={postId}
              replyLikePending={replyLikePending}
              isSubmitting={isSubmitting}
              onEditSave={handleEditSave}
              onLike={handleReplyLike}
              onReplySubmit={handleReplySubmit}
            />
          ))}

          {/* Empty state */}
          {visibleReplies.length === 0 && (
            <p className="replies-empty-state">
              No replies yet. Be the first to reply!
            </p>
          )}

          {/* Load more */}
          {visibleReplies.length > displayCount && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setDisplayCount((prev) => prev + 8)}
              className="show-more-replies"
            >
              View {Math.min(8, visibleReplies.length - displayCount)} more replies
            </Button>
          )}

          {/* Show less */}
          {displayCount > 8 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDisplayCount(8)}
              className="show-less-replies"
            >
              Show less
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

ReplyBox.propTypes = {
  postId: PropTypes.string,
  commentId: PropTypes.string.isRequired,
  isVisible: PropTypes.bool.isRequired,
  replyToUser: PropTypes.shape({
    userId: PropTypes.string,
    name: PropTypes.string,
  }),
};

export default React.memo(ReplyBox);
